import { FC, useEffect } from 'react'
import { Box, Button, H1, H3, makeStyles, Typography } from '@perk-ui/core'
import { CheckCircle, Home } from 'react-feather'
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '960px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  formHeader: {
    marginBottom: '32px',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: '#D1FAE5',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
  },
  successIcon: {
    color: '#059669',
    width: '60px',
    height: '60px',
  },
  title: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#212121',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: 400,
    color: '#6B7280',
    lineHeight: '26px',
    marginBottom: theme.spacing(4),
  },
  description: {
    fontSize: '16px',
    color: '#6B7280',
    lineHeight: '24px',
    marginBottom: theme.spacing(4),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    height: '48px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0, 4),
  },
  secondaryButton: {
    height: '48px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0, 4),
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#FFFFFF',
    },
  },
  nextStepsBox: {
    backgroundColor: '#F8FAFC',
    borderRadius: '12px',
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
    textAlign: 'left',
  },
  nextStepsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#212121',
    marginBottom: theme.spacing(2),
  },
  nextStepsList: {
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: '20px',
    '& ul': {
      paddingLeft: theme.spacing(2),
      marginBottom: 0,
      '& li': {
        marginBottom: theme.spacing(1),
      },
    },
  },
}))

const ProviderComplete: FC = () => {
  const classes = useStyles()
  const { push } = useHistory()

  // Clear provider-related localStorage items on completion
  useEffect(() => {
    localStorage.removeItem('providerImpactToken')
    // Clear the clinic creation flag when reaching the complete page
    // This ensures the flag is cleared even if user logs out without clicking Go to Dashboard
    localStorage.removeItem('isCreatingClinic')
  }, [])

  const handleGoToDashboard = () => {
    // Clear the clinic creation flag when user explicitly leaves provider flow
    localStorage.removeItem('isCreatingClinic')
    push('/home')
  }

  const handleContactSupport = () => {
    window.location.href =
      'mailto:support@databiologics.com?subject=Welcome - New Provider Setup'
  }

  return (
    <Box className={classes.formCard}>
      <Box className={classes.iconContainer}>
        <CheckCircle className={classes.successIcon} />
      </Box>

      <H1 className={classes.title}>Welcome to DataBiologics!</H1>

      <H3 className={classes.subtitle}>
        Your account has been successfully created and your provider profile is
        set up.
      </H3>

      <Typography className={classes.description}>
        You&apos;re now ready to start tracking patient outcomes and
        contributing to the advancement of regenerative medicine. Our team will
        be in touch shortly to help you get the most out of your DataBiologics
        experience.
      </Typography>

      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          startIcon={<Home size={18} />}
          onClick={handleGoToDashboard}
        >
          Go to Dashboard
        </Button>

        <Button
          variant="outlined"
          className={classes.secondaryButton}
          onClick={handleContactSupport}
        >
          Contact Support
        </Button>
      </Box>

      <Box className={classes.nextStepsBox}>
        <Typography className={classes.nextStepsTitle}>
          What happens next?
        </Typography>
        <Box className={classes.nextStepsList}>
          <ul>
            <li>
              Our team will review your application and reach out within 1-2
              business days
            </li>
            <li>
              You&apos;ll receive setup instructions and training resources via
              email
            </li>
            <li>
              We&apos;ll schedule a personalized provider call to get you
              started
            </li>
            <li>
              Access to patient tracking tools and outcome analytics will be
              activated
            </li>
          </ul>
        </Box>
      </Box>
    </Box>
  )
}

export default ProviderComplete
